import i18next from 'i18next'
import React from 'react'
import {
  any,
  AsyncState,
  fromJSX,
  Fun,
  IOWidget,
  loadedAsyncState,
  onlyIf,
} from 'widgets-for-react'
import { UserSubscriptions } from '../../../../subscription/subscriptionState'
import { wrapHtml4 } from '../../../../utils/safe-type-casting'
import { CollectionsLoaderState } from '../../collections/collectionsLoader/collectionsLoaderState'
import { categoriesFilterWidget } from '../../filters/categoriesFilter/categoriesFilterWidget'
import {
  FiltersLoaderState,
  MainCategory,
} from '../../filters/filtersLoader/filtersLoaderState'
import {
  CategoriesFilter,
  filteredUrl,
  TopFilters,
} from '../../filters/filtersUtils'
import { topFiltersWidget } from '../../filters/topFilters/topFiltersWidget'
import { paginationWidget } from '../../pagination/paginationWidget'
import { paywallWidget } from '../../paywall/paywallWidget'
import { RootLayout } from '../../rootLayout'
import { ViewListenerState } from '../../viewListener/viewListenerState'
import { componentSaverWidget } from '../componentSaver/componentSaverWidget'
import { ComponentsCountLoaderState } from '../componentsCountLoader/componentsCountLoaderState'
import { componentTeasersWidget } from '../componentTeasers/componentTeasersWidget'
import { ComponentOverviewWrapperLayout } from './componentOverviewLayout'
import {
  ComponentOverviewState,
  componentOverviewStateUpdaters,
} from './componentOverviewState'

export const componentsPerPage = 24

export const componentOverviewWidget =
  (
    topFilters: TopFilters,
    categoriesFilter: CategoriesFilter,
    page: number,
    isLoggedIn: boolean,
    userSubscriptionsState: AsyncState<UserSubscriptions>,
    collectionsState: CollectionsLoaderState,
    categoriesState: FiltersLoaderState,
    componentsCountState: ComponentsCountLoaderState,
    viewListenerState: ViewListenerState
  ): IOWidget<
    ComponentOverviewState,
    Fun<
      ComponentOverviewState,
      ComponentOverviewState & { reloadCollections?: boolean }
    >
  > =>
  (state) =>
    any([
      componentSaverWidget(collectionsState)(state.componentSaverState).map(
        componentOverviewStateUpdaters.componentSaverStateUpdater
      ),
      paywallWidget(isLoggedIn, userSubscriptionsState)(
        componentOverviewBaseWidget(
          topFilters,
          categoriesFilter,
          page,
          collectionsState,
          categoriesState,
          componentsCountState,
          viewListenerState,
          true,
          isLoggedIn
        )(state),
        componentOverviewBaseWidget(
          topFilters,
          categoriesFilter,
          page,
          collectionsState,
          categoriesState,
          componentsCountState,
          viewListenerState,
          false,
          isLoggedIn
        )(state)
      ),
    ])

const componentOverviewBaseWidget =
  (
    topFilters: TopFilters,
    categoriesFilter: CategoriesFilter,
    page: number,
    collectionsState: CollectionsLoaderState,
    filtersLoaderState: FiltersLoaderState,
    componentsCountState: AsyncState<number>,
    viewListenerState: ViewListenerState,
    paywalled: boolean,
    isLoggedIn: boolean
  ): IOWidget<
    ComponentOverviewState,
    Fun<
      ComponentOverviewState,
      ComponentOverviewState & { reloadCollections?: boolean }
    >
  > =>
  (state) => {
    if (
      filtersLoaderState.kind === 'loading' ||
      componentsCountState.kind === 'loading'
    )
      return fromJSX(() => (
        <RootLayout.Loader hasWrapper={true} showText={true} />
      ))
    if (
      filtersLoaderState.kind === 'loaded' &&
      componentsCountState.kind === 'loaded'
    ) {
      const filters = filtersLoaderState.value
      const paginationState = state.paginationState
      const selectedCategories = getSelectedCategories(
        categoriesFilter,
        filters.MainCategories
      )
      return wrapHtml4([
        topFiltersWidget(
          filters.Techniques,
          filters.Appearances,
          filters.Textures,
          filters.Allergens,
          [
            {
              Translation: i18next.t('componentFilter:basicComponents'),
              Title: 'basic',
            },
            {
              Translation: i18next.t('componentFilter:ownComponents'),
              Title: 'own',
            },
          ],
          categoriesFilter,
          topFilters,
          viewListenerState,
          state.paginationState,
          filteredUrl(
            `/${i18next.language === 'nl' ? 'nl/componenten' : 'en/components'}`
          )
        )(state.topFiltersState).map(
          componentOverviewStateUpdaters.topFiltersStateUpdater
        ),
        categoriesFilterWidget(
          topFilters,
          filters.MainCategories,
          selectedCategories,
          viewListenerState,
          paywalled
        )(state.categoriesFilterState).map(
          componentOverviewStateUpdaters.categoriesFilterStateUpdater
        ),
        componentTeasersWidget(
          collectionsState,
          'component-overview',
          isLoggedIn ? 'link-save' : 'link-only',
          undefined,
          false,
          false
        )(
          paywalled && state.componentTeasersState.kind == 'loaded'
            ? loadedAsyncState(state.componentTeasersState.value.slice(0, 4))
            : state.componentTeasersState
        ).map(componentOverviewStateUpdaters.componentTeasersStateUpdater),
        onlyIf(
          !paywalled,
          paginationWidget(page - 1, componentsPerPage, (newPage: number) =>
            filteredUrl(
              `/${
                i18next.language === 'nl' ? 'nl/componenten' : 'en/components'
              }`
            )({
              categoriesFilter: categoriesFilter,
              topFilters: topFilters,
              page: newPage + 1,
            })
          )(state.paginationState).map(
            componentOverviewStateUpdaters.paginationStateUpdater
          )
        ),
      ])((html) => (
        <ComponentOverviewWrapperLayout
          totalComponentsCount={
            componentsCountState.kind == 'loaded'
              ? componentsCountState.value
              : undefined
          }
          componentsCount={
            paginationState.kind === 'loaded'
              ? paginationState.value
              : undefined
          }
          topFiltersContent={html[0]}
          categoriesFilterContent={html[1]}
          componentTeasersContent={html[2]}
          paginationContent={html[3]}
          view={viewListenerState}
        />
      ))
    }
    return fromJSX(() => <RootLayout.Error />)
  }

export const getSelectedCategories = (
  filter: CategoriesFilter,
  categories: MainCategory[]
) => {
  const selectedCategory = categories.find(
    (category) => category.Title === filter.category
  )
  const selectedSubcategory = selectedCategory?.Subcategories.find(
    (subcategory) => subcategory.Title === filter.subcategory
  )
  const selectedIngredient = selectedSubcategory?.Ingredients.find(
    (ingredient) => ingredient.Title === filter.ingredient
  )
  return {
    category: selectedCategory,
    subcategory: selectedSubcategory,
    ingredient: selectedIngredient,
  }
}
